import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { CommonModule, Location } from "@angular/common";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { Subscription } from "rxjs";
import swal from "sweetalert2";

import { AuthService } from "src/app/security/auth.service";
import { tieneAccesoMenu } from "src/app/shared/menu/acceso-menu";
import {
  BscCiclo,
  BscCicloTrimestre,
  BscEjercicioTrimestre,
  BscRes,
  EstatusTrimestreBsc,
  FiltroBandejaBsc,
  SemaforoBsc,
} from "src/app/shared/model/bsc/bsc.model";
import { Msg } from "src/app/utilerias/mensajes/Msg";
import { BscService } from "../bsc.service";
import { ESTATUS_TRIMESTRE, EstadoBsc, SEMAFORO } from "../bsc-estados";
import { SIN_DATO, diaMes, mesesDelTrimestre, numero, pct, pts } from "../bsc-formato";
import { SelectorCorteBscComponent, SeleccionCorteBsc } from "../selector-corte/selector-corte-bsc.component";
import { CambioEnvioBsc, DetalleEnvioBscComponent } from "./detalle-envio-bsc.component";

/**
 * Qué enseña la pantalla para el año y el trimestre:
 *   CARGANDO   la consulta en vuelo (la primera vez, o al cambiar de año o trimestre);
 *   ERROR      bandeja.do falló (sin la hoja revision, sin sesión, sin conexión);
 *   SIN_CICLO  el año no está dado de alta o no se pudo leer su ciclo (el porqué va en avisos);
 *   DATOS      la bandeja con sus filtros y el detalle del envío elegido.
 */
type EstadoBandeja = "CARGANDO" | "ERROR" | "SIN_CICLO" | "DATOS";

/** Un botón del filtro por estado, con cuántos renglones tiene (conteos del servidor). */
interface OpcionFiltro {
  valor: FiltroBandejaBsc;
  texto: string;
  cuantos: number;
}

/** Un renglón de la tabla, armado UNA vez por respuesta. */
interface FilaBandeja {
  id: number;
  area: string;
  /** Lo envió quien consulta o está asignado al área: lo autoriza otra persona. */
  propio: boolean;
  /** El estatus, o «Sin enviar» si ya pasó la fecha límite sin enviarse. */
  estado: EstadoBsc<string>;
  /** "En captura · venció el 15 jul", "vence el 15 jul", "sin fecha límite"… */
  estadoNota: string;
  enviadoPor: string;
  enviadoCuando: string;
  alcance: string;
  /** "de 50%" (lo programado). */
  programado: string;
  /** "En meta · 0 pts" (el semáforo en palabras, no solo en color). */
  diferencia: string;
  semaforo: EstadoBsc<SemaforoBsc> | null;
  /** ENVIADO: espera a Dirección. */
  porRevisar: boolean;
  /** PENDIENTE, EN_CAPTURA o REGRESADO: entra en «Recordar a pendientes». */
  porEnviar: boolean;
  etiquetaAccion: string;
  renglon: BscEjercicioTrimestre;
}

/** Lo que dejó «Recordar a pendientes». */
interface ResultadoRecordar {
  ok: boolean;
  texto: string;
  avisos: string[];
}

const TRIMESTRES = [1, 2, 3, 4];

/**
 * Los filtros, en el orden de la maqueta (Todas, Por revisar, Autorizados,
 * Sin enviar) y después los demás estatus. Los cuatro primeros siempre se
 * ven; los demás solo si tienen renglones o están elegidos.
 */
const FILTROS: { valor: FiltroBandejaBsc; texto: string; fijo: boolean }[] = [
  { valor: "TODOS", texto: "Todas", fijo: true },
  { valor: "ENVIADO", texto: "Por revisar", fijo: true },
  { valor: "AUTORIZADO", texto: "Autorizados", fijo: true },
  { valor: "SIN_ENVIAR", texto: "Sin enviar", fijo: true },
  { valor: "EN_CAPTURA", texto: "En captura", fijo: false },
  { valor: "REGRESADO", texto: "Regresados", fijo: false },
  { valor: "PENDIENTE", texto: "Pendientes", fijo: false },
];

/** Los que «Recordar a pendientes» avisa (BscRevisionBuss.SIN_ENVIAR_AUN). */
const POR_ENVIAR = new Set<EstatusTrimestreBsc>(["PENDIENTE", "EN_CAPTURA", "REGRESADO"]);

/** En la tabla, «Enviado» a secas como la maqueta: la columna comparte la fila con el detalle. */
const TEXTO_CORTO: Partial<Record<EstatusTrimestreBsc, string>> = { ENVIADO: "Enviado" };

/** «Sin enviar» no es un estatus: es la bandera sinEnviar (ver ESTATUS_TRIMESTRE). */
const SIN_ENVIAR: EstadoBsc<string> = {
  valor: "SIN_ENVIAR",
  texto: "Sin enviar",
  clase: "bsc-en-vencida",
  icono: "event_busy",
};

const RUTA = {
  tablero: "/admin/bsc/tablero",
  parametros: "/admin/bsc/parametros",
  planeacion: "/admin/bsc/planeacion",
};

/**
 * BSC · C2 Bandeja de revisión de Dirección (docs/BSC.md §4, §7 y Anexo B,
 * maqueta «C2-BandejaRevision»; F6.6).
 *
 * Un renglón por área con planeación autorizada en el trimestre del año
 * (bandeja.do, BscRevisionBuss.bandeja): estatus, quién y cuándo lo envió,
 * la fecha límite («sin fecha límite» si T4 no la fijó), el alcance contra lo
 * programado y el semáforo (snapshot si está AUTORIZADO; en vivo si no). Los
 * filtros por estado cuentan con `conteos` del servidor y filtran en la
 * pantalla con la regla de BscRevisionBuss.filtrar (un estatus, o SIN_ENVIAR
 * = la bandera sinEnviar): se pide la bandeja completa una vez por año y
 * trimestre. «Recordar a pendientes» (recordar.do) avisa en la campana a las
 * áreas que siguen sin enviar.
 *
 * A la derecha, como la maqueta, el detalle del envío elegido
 * (DetalleEnvioBscComponent): KPIs del trimestre, los que quedan bajo lo
 * programado, el comentario del envío, la bitácora y Autorizar / Regresar /
 * Reabrir con el cuadro del PIN de T3. En pantallas angostas va debajo.
 *
 * Sin trimestre en la URL, el servidor elige (el primero con un envío por
 * revisar o el que toca por la fecha) y la pantalla lo pone en el combo. Año,
 * trimestre, filtro y el envío abierto viajan en la URL
 * (?anio=&trimestre=&estatus=&id=): así llegan las tiles de entregas de A1 y A2.
 *
 * Base visual: table-page (hero, barra del selector con el trimestre
 * proyectado como el área de C1, tarjeta con la tabla, estados vacíos).
 */
@Component({
  selector: "app-bandeja-revision-bsc",
  standalone: true,
  templateUrl: "./bandeja-revision-bsc.component.html",
  // dashboard-tarjetas.css para el trimestre que se proyecta en la barra del
  // selector: lleva el _ngcontent de esta pantalla (como C1 y T3).
  styleUrls: [
    "../../dashboard/shared/dashboard-tarjetas.css",
    "../bsc.shared.css",
    "./bandeja-revision-bsc.component.css",
  ],
  imports: [CommonModule, RouterLink, MatButtonModule, MatIconModule, SelectorCorteBscComponent, DetalleEnvioBscComponent],
})
export class BandejaRevisionBscComponent implements OnInit, OnDestroy {
  @ViewChild(DetalleEnvioBscComponent) detalle?: DetalleEnvioBscComponent;
  @ViewChild(SelectorCorteBscComponent) selector?: SelectorCorteBscComponent;

  readonly ruta = RUTA;
  readonly trimestres = TRIMESTRES;

  anioInicial: number | null = null;
  anio: number | null = null;
  /** El trimestre que se pidió (nulo = que elija el servidor). */
  private trimestrePedido: number | null = null;
  /** El de la bandeja que se ve (res.trimestre). */
  trimestre: number | null = null;
  filtro: FiltroBandejaBsc = "TODOS";
  /** El envío abierto en el detalle (id de bsc_ejercicio_trimestre). */
  seleccion: number | null = null;

  estado: EstadoBandeja = "CARGANDO";
  loading = false;
  error = "";
  ciclo: BscCiclo | null = null;
  avisos: string[] = [];
  puedeConfigurar = false;
  verTablero = false;
  verPlaneacion = false;

  /* ---------- Lo que se pinta, armado una vez por respuesta ---------- */
  filas: FilaBandeja[] = [];
  filasVisibles: FilaBandeja[] = [];
  filtros: OpcionFiltro[] = [];
  /** "3 por revisar · 2 autorizados · 1 vencido · fecha límite de envío 15 jul". */
  resumen = "";
  /** "15 jul" o vacío (sin fecha límite). */
  limite = "";
  recordando = false;
  resultadoRecordar: ResultadoRecordar | null = null;

  private conteos: Partial<Record<FiltroBandejaBsc, number>> = {};
  private primeraConsulta = true;
  private consulta?: Subscription;
  private recordatorio?: Subscription;
  private query$?: Subscription;
  /** La pregunta de «Recordar a pendientes» está abierta (se cierra al salir). */
  private preguntaAbierta = false;
  private destruido = false;

  constructor(
    private readonly bscService: BscService,
    private readonly authService: AuthService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly location: Location,
    private readonly msg: Msg,
    private readonly cdr: ChangeDetectorRef,
    private readonly host: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    this.anioInicial = anioValido(params.get("anio"));
    this.trimestrePedido = trimestreValido(params.get("trimestre"));
    this.filtro = filtroValido(params.get("estatus")) ?? "TODOS";
    this.seleccion = idValido(params.get("id"));
    const menus = this.authService.getMenuList();
    this.verTablero = tieneAccesoMenu(menus, RUTA.tablero);
    this.verPlaneacion = tieneAccesoMenu(menus, RUTA.planeacion);
    // Angular reutiliza la pantalla si solo cambian los parámetros (un enlace
    // pegado, el historial del navegador): se abre lo que piden. La pantalla
    // pinta su URL con replaceState, que no pasa por aquí.
    this.query$ = this.route.queryParamMap.subscribe((q) => {
      if (!this.primeraConsulta) {
        this.sigueParametros(
          anioValido(q.get("anio")),
          trimestreValido(q.get("trimestre")),
          filtroValido(q.get("estatus")) ?? "TODOS",
          idValido(q.get("id")),
        );
      }
    });
  }

  ngOnDestroy(): void {
    this.destruido = true;
    this.consulta?.unsubscribe();
    this.recordatorio?.unsubscribe();
    this.query$?.unsubscribe();
    // La pregunta (Swal) vive fuera de la pantalla y sobrevive al cambio de ruta, como en T1 y T3.
    if (this.preguntaAbierta && swal.isVisible()) {
      swal.close();
    }
  }

  private sigueParametros(anio: number | null, q: number | null, filtro: FiltroBandejaBsc, id: number | null): void {
    if ((anio != null && anio !== this.anio) || (q != null && q !== this.trimestre)) {
      if (anio != null && anio !== this.anio) {
        this.anio = anio;
        this.selector?.fijarAnio(anio);
      }
      this.trimestrePedido = q;
      this.filtro = filtro;
      this.seleccion = id;
      this.consultar(false, true);
      return;
    }
    if (filtro === this.filtro && (id == null || id === this.seleccion)) {
      return;
    }
    this.filtro = filtro;
    this.filasVisibles = this.filtradas();
    this.filtros = this.armaFiltros();
    if (id != null && this.filas.some((f) => f.id === id)) {
      this.seleccion = id;
    } else {
      this.eligeSeleccion(false);
    }
    this.pintaUrl();
    this.cdr.detectChanges();
  }

  /* ============================ Año, trimestre y filtro ============================ */

  onSeleccion(seleccion: SeleccionCorteBsc): void {
    if (seleccion.anio === this.anio && !this.primeraConsulta) {
      return;
    }
    const otroAnio = this.anio != null && seleccion.anio !== this.anio;
    this.anio = seleccion.anio;
    if (otroAnio) {
      // Otro año: que el servidor elija el trimestre que toca revisar.
      this.trimestrePedido = null;
      this.seleccion = null;
    }
    this.consultar();
  }

  cambiaTrimestre(evento: Event): void {
    const q = trimestreValido((evento.target as HTMLSelectElement).value);
    if (q == null || q === this.trimestre) {
      return;
    }
    this.trimestrePedido = q;
    this.seleccion = null;
    this.consultar();
  }

  cambiaFiltro(valor: FiltroBandejaBsc): void {
    if (valor === this.filtro) {
      return;
    }
    this.filtro = valor;
    this.filasVisibles = this.filtradas();
    this.filtros = this.armaFiltros();
    this.eligeSeleccion(false);
    this.pintaUrl();
    this.cdr.detectChanges();
  }

  /** «Revisar» / «Ver» de un renglón: lo abre en el detalle y lleva ahí el foco (debajo, en pantallas angostas). */
  abre(fila: FilaBandeja): void {
    if (this.seleccion !== fila.id) {
      this.seleccion = fila.id;
      this.pintaUrl();
      this.cdr.detectChanges();
    }
    setTimeout(() => this.detalle?.enfocar());
  }

  /** Clic en cualquier parte del renglón (el botón es el que sirve con teclado). */
  clicFila(fila: FilaBandeja, evento: MouseEvent): void {
    if ((evento.target as HTMLElement | null)?.closest("a, button")) {
      return;
    }
    this.abre(fila);
  }

  /** El detalle resolvió, reabrió o encontró el envío cambiado: la bandeja se vuelve a pedir sin moverse. */
  onCambioDetalle(_cambio: CambioEnvioBsc): void {
    this.consultar(true);
  }

  /* ============================ Consulta ============================ */

  /**
   * @param silencioso después de resolver en el detalle: la tabla se queda
   *        como está mientras contesta y el envío abierto no cambia aunque
   *        el filtro ya no lo muestre.
   * @param conservar el envío elegido se respeta aunque el filtro no lo
   *        muestre (el de un enlace).
   */
  consultar(silencioso = false, conservar = false): void {
    if (this.anio == null) {
      return;
    }
    const anio = this.anio;
    const pedido = this.trimestrePedido;
    this.consulta?.unsubscribe();
    this.loading = true;
    this.error = "";
    if (!silencioso) {
      this.estado = "CARGANDO";
      this.resultadoRecordar = null;
    }
    this.cdr.detectChanges();

    this.consulta = this.bscService.revisionBandeja(anio, pedido, null).subscribe({
      next: (res) => {
        if (anio !== this.anio || pedido !== this.trimestrePedido) {
          return;
        }
        this.loading = false;
        if (!res || res.codigoError) {
          this.falla(mensaje(res, "No se pudo consultar la bandeja de revisión."));
          return;
        }
        // La selección del enlace se respeta la primera vez, aunque el filtro no la muestre.
        const respeta = silencioso || conservar || this.primeraConsulta;
        this.primeraConsulta = false;
        this.aplica(res, respeta);
        this.cdr.detectChanges();
      },
      error: () => {
        if (anio !== this.anio || pedido !== this.trimestrePedido) {
          return;
        }
        this.loading = false;
        this.falla("No se pudo consultar la bandeja de revisión: sin conexión con el servidor.");
      },
    });
  }

  private falla(texto: string): void {
    this.primeraConsulta = false;
    this.error = texto;
    this.estado = "ERROR";
    this.filas = [];
    this.filasVisibles = [];
    this.filtros = [];
    this.avisos = [];
    this.ciclo = null;
    this.seleccion = null;
    this.cdr.detectChanges();
  }

  private aplica(res: BscRes, conservar: boolean): void {
    this.trimestre = trimestreValido(`${res.trimestre ?? ""}`) ?? this.trimestrePedido ?? 1;
    this.ciclo = res.ciclo ?? null;
    this.avisos = res.avisos ?? [];
    this.puedeConfigurar = res.puedeConfigurar === true;
    this.conteos = res.conteos ?? {};
    const fecha = fechaDelTrimestre(res.trimestresCiclo, this.trimestre);
    this.limite = fecha ? diaMes(fecha, this.anio) : "";
    this.filas = (res.bandeja ?? []).filter((t) => t.id != null).map((t) => this.fila(t));
    this.filasVisibles = this.filtradas();
    this.filtros = this.armaFiltros();
    this.resumen = this.armaResumen();
    this.estado = this.ciclo ? "DATOS" : "SIN_CICLO";
    if (this.estado === "DATOS") {
      this.eligeSeleccion(conservar);
    } else {
      this.seleccion = null;
    }
    this.pintaUrl();
  }

  /**
   * El envío que se abre: el elegido si sigue a la vista (o, con `conservar`,
   * si sigue en la bandeja aunque el filtro no lo muestre); si no, el primero
   * por revisar del filtro y, sin ninguno, el primero.
   */
  private eligeSeleccion(conservar: boolean): void {
    const sel = this.seleccion;
    if (sel != null) {
      if (this.filasVisibles.some((f) => f.id === sel)) {
        return;
      }
      if (conservar && this.filas.some((f) => f.id === sel)) {
        return;
      }
    }
    this.seleccion = (this.filasVisibles.find((f) => f.porRevisar) ?? this.filasVisibles[0])?.id ?? null;
  }

  /**
   * La URL dice lo que se ve, sin sumar pasos al historial. Con
   * Location.replaceState y no con router.navigate (C1): cada navegación
   * termina en el scrollTo(0, 0) de AdminLayoutComponent y elegir un renglón
   * de abajo subiría la página.
   */
  private pintaUrl(): void {
    if (this.anio == null || this.destruido) {
      return;
    }
    const url = this.router.createUrlTree([], {
      relativeTo: this.route,
      queryParams: {
        anio: this.anio,
        trimestre: this.trimestre,
        estatus: this.filtro === "TODOS" ? null : this.filtro,
        id: this.seleccion,
      },
    });
    this.location.replaceState(this.router.serializeUrl(url));
  }

  /* ============================ Recordar a pendientes ============================ */

  get porEnviar(): FilaBandeja[] {
    return this.filas.filter((f) => f.porEnviar);
  }

  get cicloCerrado(): boolean {
    return this.ciclo?.estatus === "CERRADO";
  }

  /** Se ofrece con el año abierto y alguna área con el trimestre sin enviar. */
  get puedeRecordar(): boolean {
    return this.estado === "DATOS" && !this.cicloCerrado && this.porEnviar.length > 0;
  }

  recordar(): void {
    if (!this.puedeRecordar || this.recordando || this.anio == null || this.trimestre == null) {
      return;
    }
    const areas = this.porEnviar.map((f) => f.area);
    const n = areas.length;
    const limite = this.limite ? `. La fecha límite de envío es el ${this.limite}.` : ". No tiene fecha límite.";
    const texto =
      `Se avisará en la campana del COS a los responsables y capturistas de ${cuantasAreas(n)} ` +
      `con Q${this.trimestre} ${this.anio} sin enviar: ${enumera(areas)}${limite}`;
    this.preguntaAbierta = true;
    this.msg.questionCall(texto, "Recordar a pendientes", (r: { isConfirmed?: boolean }) => {
      this.preguntaAbierta = false;
      if (!this.destruido && r?.isConfirmed === true) {
        this.recordarYa();
      }
    });
  }

  private recordarYa(): void {
    const anio = this.anio;
    const q = this.trimestre;
    if (anio == null || q == null || this.recordando) {
      return;
    }
    this.recordando = true;
    this.resultadoRecordar = null;
    this.cdr.detectChanges();
    this.recordatorio = this.bscService.revisionRecordar(anio, q).subscribe({
      next: (res) => {
        this.recordando = false;
        if (!res || res.codigoError) {
          this.resultadoRecordar = { ok: false, texto: mensaje(res, "No se pudo enviar el recordatorio."), avisos: [] };
        } else {
          const avisadas = (res.bandeja ?? []).map((t) => t.area || SIN_DATO);
          const enviados = res.enviados ?? avisadas.length;
          const cuales = avisadas.length > 0 ? `: ${enumera(avisadas)}` : "";
          this.resultadoRecordar = {
            ok: enviados > 0,
            texto:
              enviados > 0
                ? `Se envió el recordatorio de Q${q} ${anio} a ${cuantasAreas(enviados)}${cuales}.`
                : "No se envió ningún recordatorio.",
            avisos: res.avisos ?? [],
          };
        }
        this.cdr.detectChanges();
        this.enfoca(".revision-bsc__recordatorio");
      },
      error: () => {
        this.recordando = false;
        this.resultadoRecordar = {
          ok: false,
          texto: "No se pudo enviar el recordatorio: sin conexión con el servidor.",
          avisos: [],
        };
        this.cdr.detectChanges();
        this.enfoca(".revision-bsc__recordatorio");
      },
    });
  }

  /* ============================ Presentación ============================ */

  get titulo(): string {
    return this.trimestre != null && this.anio != null
      ? `Revisión de cierres · Q${this.trimestre} ${this.anio}`
      : "Revisión de cierres";
  }

  get filaSeleccionada(): FilaBandeja | null {
    return this.filas.find((f) => f.id === this.seleccion) ?? null;
  }

  /** La selección no se ve con el filtro actual (se resolvió y salió de «Por revisar»). */
  get seleccionOculta(): boolean {
    return this.seleccion != null && !this.filasVisibles.some((f) => f.id === this.seleccion);
  }

  get textoFiltro(): string {
    return FILTROS.find((f) => f.valor === this.filtro)?.texto ?? "";
  }

  /** "enero a marzo" para el combo y la barra. */
  meses(q: number): string {
    return mesesDelTrimestre(q);
  }

  /** El renglón de la barra del selector: cuántas áreas y qué meses. */
  get resumenBarra(): string {
    const n = this.filas.length;
    return `${n} ${n === 1 ? "área" : "áreas"} con planeación autorizada`;
  }

  get queryTablero(): Record<string, string | number | null> {
    return { anio: this.anio, corte: this.trimestre != null ? `${this.trimestre}` : null };
  }

  private filtradas(): FilaBandeja[] {
    const f = this.filtro;
    return this.filas.filter(
      (x) => f === "TODOS" || (f === "SIN_ENVIAR" ? x.renglon.sinEnviar === true : x.renglon.estatus === f),
    );
  }

  private armaFiltros(): OpcionFiltro[] {
    return FILTROS.map((f) => ({ valor: f.valor, texto: f.texto, cuantos: this.conteos[f.valor] ?? this.cuenta(f.valor) }))
      .filter((f) => FILTROS.find((x) => x.valor === f.valor)?.fijo || f.cuantos > 0 || f.valor === this.filtro);
  }

  /** Por si el servidor no mandó los conteos: los mismos sobre los renglones. */
  private cuenta(valor: FiltroBandejaBsc): number {
    return this.filas.filter(
      (x) =>
        valor === "TODOS" ||
        (valor === "SIN_ENVIAR" ? x.renglon.sinEnviar === true : x.renglon.estatus === valor),
    ).length;
  }

  private armaResumen(): string {
    const c = (valor: FiltroBandejaBsc) => this.conteos[valor] ?? this.cuenta(valor);
    const plural = (n: number, uno: string, varios: string) => `${n} ${n === 1 ? uno : varios}`;
    const partes = [
      `${c("ENVIADO")} por revisar`,
      plural(c("AUTORIZADO"), "autorizado", "autorizados"),
    ];
    if (c("REGRESADO") > 0) {
      partes.push(plural(c("REGRESADO"), "regresado", "regresados"));
    }
    const enCaptura = c("EN_CAPTURA") + c("PENDIENTE");
    if (enCaptura > 0) {
      partes.push(`${enCaptura} en captura`);
    }
    if (c("SIN_ENVIAR") > 0) {
      partes.push(plural(c("SIN_ENVIAR"), "vencido", "vencidos"));
    }
    partes.push(this.limite ? `fecha límite de envío ${this.limite}` : "sin fecha límite de envío");
    return partes.join(" · ");
  }

  private fila(t: BscEjercicioTrimestre): FilaBandeja {
    const q = t.trimestre ?? this.trimestre ?? 1;
    const area = (t.area ?? "").trim() || `Área ${t.idBscArea ?? ""}`.trim();
    const estatus = ESTATUS_TRIMESTRE.find((e) => e.valor === t.estatus) ?? null;
    const corto = estatus ? { ...estatus, texto: TEXTO_CORTO[estatus.valor] ?? estatus.texto } : null;
    const sinEnviar = t.sinEnviar === true;
    const estado: EstadoBsc<string> = sinEnviar
      ? SIN_ENVIAR
      : corto ?? { valor: t.estatus ?? "", texto: t.estatus ?? SIN_DATO, clase: "bsc-badge-neutro", icono: "help_outline" };
    const porEnviar = t.estatus != null && POR_ENVIAR.has(t.estatus);
    const estadoNota = this.notaDelEstado(t, estatus, porEnviar, sinEnviar);

    const enviado = !!t.fechaEnvio || !!t.usuarioEnvia;
    const real = numero(t.alcanceReal);
    const prog = numero(t.alcanceProgramado);
    const semaforo = SEMAFORO.find((s) => s.valor === t.semaforo) ?? null;
    const enPalabras = semaforo ? semaforo.texto + " · " : "";
    const diferencia =
      real != null && prog != null
        ? `${enPalabras}${pts(real - prog)}`
        : semaforo?.texto ?? "";
    const sinEnvio = sinEnviar ? "vencido" : "";
    return {
      id: t.id as number,
      area,
      propio: t.esPropio === true,
      estado,
      estadoNota,
      enviadoPor: enviado ? (t.usuarioEnvia ?? "").trim() || SIN_DATO : SIN_DATO,
      enviadoCuando: enviado ? sinCorte(diaMes(t.fechaEnvio, this.anio, "")) : sinEnvio,
      alcance: real == null ? SIN_DATO : pct(real),
      programado: prog == null ? "" : `de ${pct(prog)}`,
      diferencia,
      semaforo,
      porRevisar: t.estatus === "ENVIADO",
      porEnviar,
      etiquetaAccion: `${t.estatus === "ENVIADO" ? "Revisar" : "Ver"} Q${q} de ${area}`,
      renglon: t,
    };
  }

  /** Lo que acompaña al estatus: la fecha límite de lo que falta enviar, o quién lo resolvió. */
  private notaDelEstado(
    t: BscEjercicioTrimestre,
    estatus: (typeof ESTATUS_TRIMESTRE)[number] | null,
    porEnviar: boolean,
    sinEnviar: boolean,
  ): string {
    if (porEnviar) {
      const fecha = t.fechaLimiteEnvio ? sinCorte(diaMes(t.fechaLimiteEnvio, this.anio)) : "";
      const limite = textoLimite(fecha, sinEnviar);
      return sinEnviar && estatus ? `${estatus.texto} · ${limite}` : limite;
    }
    if (t.estatus === "AUTORIZADO") {
      return t.origen === "IMPORTACION"
        ? "Importado del FOR-011"
        : [t.usuarioResuelve?.trim(), sinCorte(diaMes(t.fechaResolucion, this.anio, ""))].filter(Boolean).join(" · ");
    }
    return "";
  }

  /* ============================ trackBy ============================ */

  trackFila(_: number, f: FilaBandeja): number {
    return f.id;
  }

  trackFiltro(_: number, f: OpcionFiltro): string {
    return f.valor;
  }

  trackTexto(i: number): number {
    return i;
  }

  private enfoca(selector: string): void {
    setTimeout(() => this.host.nativeElement.querySelector<HTMLElement>(selector)?.focus());
  }
}

/* ============================ Funciones puras ============================ */

function mensaje(res: BscRes | null | undefined, porOmision: string): string {
  // AUTH-001 viaja en un 200 con codigoError: ningún interceptor lo ve.
  if (res?.codigoError === "AUTH-001") {
    return "Tu sesión caducó. Vuelve a entrar.";
  }
  return res?.mensajeError || porOmision;
}

/**
 * Una lista de áreas para una frase: "A", "A y B", "A, B y C", "A, B, C, D y 2
 * más" (nombres() de bsc-formato.ts corta en dos).
 */
function enumera(lista: string[], tope = 4): string {
  if (lista.length <= 1) {
    return lista.join("");
  }
  if (lista.length <= tope) {
    return `${lista.slice(0, -1).join(", ")} y ${lista.at(-1)}`;
  }
  return `${lista.slice(0, tope).join(", ")} y ${lista.length - tope} más`;
}

/** "1 área" o "N áreas". */
function cuantasAreas(n: number): string {
  return n === 1 ? "1 área" : `${n} áreas`;
}

/** "sin fecha límite", "venció el 15 jul" o "vence el 15 jul". */
function textoLimite(fecha: string, sinEnviar: boolean): string {
  if (!fecha) {
    return "sin fecha límite";
  }
  return sinEnviar ? `venció el ${fecha}` : `vence el ${fecha}`;
}

/** "15 jul" sin partirse en dos renglones de la tabla. */
function sinCorte(fecha: string): string {
  return fecha.replaceAll(" ", "\u00a0");
}

function fechaDelTrimestre(fechas: BscCicloTrimestre[] | undefined, q: number): string | null {
  return (fechas ?? []).find((f) => f.trimestre === q)?.fechaLimiteEnvio ?? null;
}

function anioValido(texto: string | null): number | null {
  const n = Number(texto);
  return texto && Number.isInteger(n) && n > 1900 && n < 3000 ? n : null;
}

function trimestreValido(texto: string | null): number | null {
  const n = Number(texto);
  return texto && Number.isInteger(n) && n >= 1 && n <= 4 ? n : null;
}

function idValido(texto: string | null): number | null {
  const n = Number(texto);
  return texto && Number.isInteger(n) && n > 0 ? n : null;
}

function filtroValido(texto: string | null): FiltroBandejaBsc | null {
  const t = (texto ?? "").toUpperCase();
  return FILTROS.some((f) => f.valor === t) ? (t as FiltroBandejaBsc) : null;
}
